(() => {
  const SEEL_PRODUCT_TYPE = 'SEEL-WFP';
  const SOURCE = 'seel-cart-guard';
  const INITIAL_CART_ID = 'SeelCartGuardState';

  class SeelCartGuard {
    constructor() {
      this.lastCart = this.readInitialCart();
      this.queuedCart = null;
      this.pending = null;
      this.timer = null;
      this.forceFetch = false;
      this.stateUncertain = !this.lastCart;
      this.checkoutAttempt = null;
      this.allowedForms = new WeakSet();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    start() {
      document.addEventListener('submit', this.handleSubmit, true);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== SOURCE) {
            this.schedule(event?.cartData, true);
          }
        });
      }

      this.schedule(this.lastCart, !this.lastCart);
    }

    readInitialCart() {
      const element = document.getElementById(INITIAL_CART_ID);

      if (!element) return null;

      try {
        const cart = JSON.parse(element.textContent);
        return this.isFullCart(cart) ? cart : null;
      } catch (error) {
        console.warn('Unable to read initial cart state', error);
        return null;
      }
    }

    isFullCart(cart) {
      return Boolean(
        cart &&
          Array.isArray(cart.items) &&
          typeof cart.item_count === 'number'
      );
    }

    hasNonProtectionItem(cart) {
      return cart.items.some(
        (item) =>
          item.quantity > 0 &&
          item.product_type !== SEEL_PRODUCT_TYPE
      );
    }

    needsProtectionRemoval(cart) {
      const hasProtection = cart.items.some(
        (item) =>
          item.quantity > 0 &&
          item.product_type === SEEL_PRODUCT_TYPE
      );
      const hasPhysicalItem = cart.items.some(
        (item) =>
          item.quantity > 0 &&
          item.product_type !== SEEL_PRODUCT_TYPE &&
          item.requires_shipping
      );

      return hasProtection && !hasPhysicalItem;
    }

    canCheckoutImmediately() {
      return Boolean(
        this.isFullCart(this.lastCart) &&
          !this.stateUncertain &&
          this.hasNonProtectionItem(this.lastCart) &&
          !this.needsProtectionRemoval(this.lastCart)
      );
    }

    schedule(cart = null, forceFetch = false) {
      if (this.isFullCart(cart)) {
        this.lastCart = cart;
        this.queuedCart = cart;
        this.forceFetch = false;
        this.stateUncertain = false;
      } else if (forceFetch) {
        this.forceFetch = true;
        this.stateUncertain = true;
      }

      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        this.sync().catch((error) => {
          console.error(error);
        });
      }, 0);
    }

    async getCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    }

    async performSync(cartState) {
      const cart = this.isFullCart(cartState)
        ? cartState
        : await this.getCart();

      this.lastCart = cart;
      this.stateUncertain = false;

      if (!this.needsProtectionRemoval(cart)) return cart;

      const updates = Object.fromEntries(
        cart.items
          .filter(
            (item) =>
              item.quantity > 0 &&
              item.product_type === SEEL_PRODUCT_TYPE
          )
          .map((item) => [item.key, 0])
      );
      const response = await fetch(
        window.routes?.cart_update_url ||
          `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates,
            sections: [
              'cart-drawer',
              'cart-icon-bubble',
              'main-cart-items',
              'main-cart-footer',
            ],
            sections_url: window.location.pathname,
          }),
        }
      );

      if (!response.ok) throw new Error('Protection removal failed');

      const updatedCart = await response.json();
      this.lastCart = updatedCart;
      this.refreshCart(updatedCart);

      return updatedCart;
    }

    sync(cartState = null) {
      if (this.isFullCart(cartState)) {
        this.lastCart = cartState;
        this.queuedCart = cartState;
        this.forceFetch = false;
        this.stateUncertain = false;
      }

      if (this.pending) return this.pending;

      this.pending = this.drainSyncQueue().finally(() => {
        this.pending = null;
      });

      return this.pending;
    }

    async drainSyncQueue() {
      let result = this.lastCart;

      do {
        const mustFetch =
          this.forceFetch ||
          !this.isFullCart(this.queuedCart || this.lastCart);
        const nextCart = mustFetch
          ? null
          : this.queuedCart || this.lastCart;

        this.queuedCart = null;
        this.forceFetch = false;
        result = await this.performSync(nextCart);
      } while (this.queuedCart || this.forceFetch);

      return result;
    }

    parseSection(html, selector) {
      if (!html) return null;
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);
    }

    replaceSection(cart, sectionId, selector = `#${sectionId}`) {
      const current = document.querySelector(selector);
      const replacement = this.parseSection(
        cart.sections?.[sectionId],
        selector
      );

      if (!current || !replacement) return false;

      current.innerHTML = replacement.innerHTML;
      return true;
    }

    refreshCart(cart) {
      const cartDrawer = document.querySelector('cart-drawer');
      let rendered = false;

      if (
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        cart.sections?.['cart-drawer']
      ) {
        cartDrawer.renderContents(cart);
        cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
        rendered = true;
      } else {
        rendered = this.replaceSection(
          cart,
          'cart-icon-bubble'
        ) || rendered;
      }

      rendered = this.replaceSection(cart, 'main-cart-items') || rendered;
      rendered = this.replaceSection(cart, 'main-cart-footer') || rendered;

      document
        .querySelector('cart-items')
        ?.classList.toggle('is-empty', cart.item_count === 0);
      document
        .querySelector('cart-drawer')
        ?.classList.toggle('is-empty', cart.item_count === 0);

      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData: cart,
        });
      } else if (!rendered) {
        window.location.reload();
      }
    }

    isCheckoutSubmission(form, submitter) {
      return (
        form instanceof HTMLFormElement &&
        submitter?.name === 'checkout'
      );
    }

    resumeCheckout(form, submitter) {
      const activeForm = form.isConnected
        ? form
        : document.querySelector('form[action*="/cart"]');
      const activeSubmitter = submitter?.isConnected
        ? submitter
        : activeForm?.querySelector('[name="checkout"]');

      if (!(activeForm instanceof HTMLFormElement)) {
        window.location.assign(`${window.Shopify.routes.root}checkout`);
        return;
      }

      this.allowedForms.add(activeForm);

      if (
        activeSubmitter &&
        !activeSubmitter.disabled &&
        typeof activeForm.requestSubmit === 'function'
      ) {
        try {
          activeForm.requestSubmit(activeSubmitter);
          return;
        } catch (error) {
          console.warn('Unable to resume checkout with requestSubmit', error);
        }
      }

      this.allowedForms.delete(activeForm);
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = 'Checkout';
      activeForm.append(checkoutInput);
      HTMLFormElement.prototype.submit.call(activeForm);
    }

    async resolveCheckout(form, submitter, knownUnsafe) {
      try {
        const cart = await this.sync();

        if (this.hasNonProtectionItem(cart)) {
          this.resumeCheckout(form, submitter);
        }
      } catch (error) {
        console.error(error);

        if (knownUnsafe) {
          this.showError();
          return;
        }

        this.resumeCheckout(form, submitter);
      }
    }

    handleSubmit(event) {
      const form = event.target;
      const submitter = event.submitter;

      if (!this.isCheckoutSubmission(form, submitter)) return;

      if (this.allowedForms.has(form)) {
        this.allowedForms.delete(form);
        return;
      }

      if (this.canCheckoutImmediately()) return;

      event.preventDefault();

      if (this.checkoutAttempt) return;

      const knownUnsafe = Boolean(
        this.isFullCart(this.lastCart) &&
          (!this.hasNonProtectionItem(this.lastCart) ||
            this.needsProtectionRemoval(this.lastCart))
      );

      this.checkoutAttempt = this.resolveCheckout(
        form,
        submitter,
        knownUnsafe
      ).finally(() => {
        this.checkoutAttempt = null;
      });
    }

    showError() {
      const errorElement =
        document.getElementById('CartDrawer-CartErrors') ||
        document.getElementById('cart-errors');

      if (errorElement && window.cartStrings?.error) {
        errorElement.textContent = window.cartStrings.error;
      }
    }
  }

  const guard = new SeelCartGuard();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => guard.start(), {
      once: true,
    });
  } else {
    guard.start();
  }
})();
